import type { KhoaDich } from '../../../../i18n/types';
import { NHAN_DICH_VU, NHAN_NHA_CUNG_CAP, NHAN_TINH_NANG_AI } from '../../constants.ts';
import { sanhTheo, type ChonDuoc, type SoSanhChon } from './tuy-chon-loc.ts';
import type { NhomPhanRa } from './types-thong-ke.ts';
import type { IdKemNhan, TrangThaiGia, TruyVanLuotGoi, TruyVanTrace, TuyChonLocAi } from './types-vet.ts';

/**
 * Bộ lọc dùng chung của màn Sử dụng AI (mọi tab) và của số liệu AI ở Tổng quan, và cách nó thành tham số của TỪNG
 * endpoint — port `loc-ai.ts` native.
 *
 * ── MỘT TRẠNG THÁI, MỖI TAB MỘT TẬP Ô ────────────────────────────────────
 * Mỗi endpoint nhận một tập tham số khác nhau (native `vet.py`, platform `module/VetController.java` giữ y):
 * `/vet/luot-goi` và `/vet/thong-ke-ai` nhận đủ tám, `/vet/trace` chỉ nhận `service` và `actor_id`, `/vet/luong` không
 * nhận ô nào trong số này. Tab nào chỉ HIỆN ô mà endpoint của nó thật sự áp — một ô hiện mà không lọc là một ô nói dối.
 */

export const TRUONG_LOC_AI = [
  'moHinh',
  'nhaCungCap',
  'service',
  'tinhNang',
  'nguoiDungId',
  'donViId',
  'trangThai',
  'coCanhBao',
] as const;

/** Hai ô nói về giá vốn và token thật: tenant không có (ADR-0123 mục 1, 5). */
const TRUONG_CHI_NEN_TANG: readonly string[] = ['trangThai', 'coCanhBao'];

export type TruongLocAi = (typeof TRUONG_LOC_AI)[number];

export interface LocAi {
  moHinh?: string;
  nhaCungCap?: string;
  service?: string;
  /** MÃ tính năng (snake_case), không phải nhãn — audit-service lọc theo cột `tinh_nang`. */
  tinhNang?: string;
  nguoiDungId?: string;
  donViId?: string;
  trangThai?: TrangThaiGia;
  coCanhBao?: boolean;
}

export const LOC_AI_MAC_DINH: LocAi = {};

export const TAB_SU_DUNG_AI = ['tong-quan', 'trace', 'luot-goi', 'luong'] as const;

export type TabSuDungAi = (typeof TAB_SU_DUNG_AI)[number];

/** Ô lọc mà endpoint của từng tab thật sự áp. */
export const TRUONG_THEO_TAB: Record<TabSuDungAi, readonly TruongLocAi[]> = {
  'tong-quan': TRUONG_LOC_AI,
  trace: ['service', 'nguoiDungId'],
  'luot-goi': TRUONG_LOC_AI,
  luong: [],
};

/**
 * Ô lọc của một tab theo người xem: cổng tenant bỏ hai ô chỉ nền tảng có nghĩa; nền tảng không có `AI_PRICING.READ`
 * (`xemGiaVon`) không có ô trạng thái giá vốn.
 */
export function truongCuaTab(tab: TabSuDungAi, nenTang: boolean, xemGiaVon: boolean): readonly TruongLocAi[] {
  const truong = TRUONG_THEO_TAB[tab];
  if (!nenTang) return truong.filter((k) => !TRUONG_CHI_NEN_TANG.includes(k));
  return xemGiaVon ? truong : truong.filter((k) => k !== 'trangThai');
}

type ThamSoLocLuot = Pick<
  TruyVanLuotGoi,
  'mo_hinh' | 'nha_cung_cap' | 'service' | 'tinh_nang' | 'nguoi_dung_id' | 'don_vi_id' | 'trang_thai' | 'co_canh_bao'
>;

/** Tham số lọc của `/vet/luot-goi` và `/vet/thong-ke-ai` — chỉ khoá có giá trị, để `queryKey` ổn định. */
export function thamSoLocLuot(loc: LocAi): ThamSoLocLuot {
  return {
    ...(loc.moHinh && { mo_hinh: loc.moHinh }),
    ...(loc.nhaCungCap && { nha_cung_cap: loc.nhaCungCap }),
    ...(loc.service && { service: loc.service }),
    ...(loc.tinhNang && { tinh_nang: loc.tinhNang }),
    ...(loc.nguoiDungId && { nguoi_dung_id: loc.nguoiDungId }),
    ...(loc.donViId && { don_vi_id: loc.donViId }),
    ...(loc.trangThai && { trang_thai: loc.trangThai }),
    ...(loc.coCanhBao !== undefined && { co_canh_bao: loc.coCanhBao }),
  };
}

/**
 * Tham số lọc của `/vet/trace`. `nguoiDungId` thành `actor_id`: hai cột cùng một id — `chi_phi_llm.nguoi_dung_id` được
 * ghi từ `actor_id` của chính sự kiện.
 */
export function thamSoLocTrace(loc: LocAi): Pick<TruyVanTrace, 'service' | 'actor_id'> {
  return {
    ...(loc.service && { service: loc.service }),
    ...(loc.nguoiDungId && { actor_id: loc.nguoiDungId }),
  };
}

/** Có ô lọc nào trong `truong` đang đặt không — để mời "Xoá bộ lọc". */
export function coLocAi(loc: LocAi, truong: readonly TruongLocAi[]): boolean {
  return truong.some((k) => loc[k] !== undefined && loc[k] !== '');
}

export interface TuyChonAiManHinh {
  moHinh: ChonDuoc[];
  nhaCungCap: ChonDuoc[];
  service: ChonDuoc[];
  tinhNang: ChonDuoc[];
  nguoi: ChonDuoc[];
  donVi: ChonDuoc[];
  biCat: boolean;
}

/**
 * Nhãn hiển thị của những mã kỹ thuật trên màn Sử dụng AI. `value` của ô lọc vẫn là mã gốc; chỉ nhãn đổi. Bảng ở
 * `constants.ts`, mã lạ giữ nguyên (dữ liệu nghiệp vụ không tự đoán tên).
 */
export interface NhanAi {
  service: (ma: string) => string;
  nhaCungCap: (ma: string) => string;
  /** Mã có trong bảng thì theo bảng (đổi theo ngôn ngữ); không thì nhãn chụp trên bản ghi; không có nữa thì mã. */
  tinhNang: (ma: string | null | undefined, nhanChup?: string | null) => string;
  /** `ma` có mặt khi cần phân biệt nhiều dòng cùng không có tên. */
  khongRoTen: (ma?: string) => string;
  /** Dòng không gắn với người nào (việc hệ thống, sự kiện trước đăng nhập). */
  khongXacDinh: string;
}

const SO_KY_TU_MA = 8;

export function dungNhanAi(t: (khoa: KhoaDich, thamSo?: Record<string, string>) => string): NhanAi {
  return {
    service: (ma) => {
      const khoa = NHAN_DICH_VU.get(ma);
      return khoa ? t(khoa) : ma;
    },
    nhaCungCap: (ma) => NHAN_NHA_CUNG_CAP.get(ma.toLowerCase()) ?? ma,
    tinhNang: (ma, nhanChup) => {
      const khoa = ma ? NHAN_TINH_NANG_AI.get(ma) : undefined;
      if (khoa) return t(khoa);
      return nhanChup?.trim() || ma || '';
    },
    khongRoTen: (ma) => (ma ? t('nktv.nguoi.khongRoTenKemMa', { ma }) : t('nktv.nguoi.khongRoTen')),
    khongXacDinh: t('nktv.nguoi.khongXacDinh'),
  };
}

/**
 * Người của một dòng bảng hay một bước. Có tên: tên. Có id mà không tên (đã rời tenant, tài khoản không có hồ sơ, danh bạ
 * đang lỗi): "Không rõ tên", mã để ở `title`. Không có id: chưa xác định được người.
 */
export function nhanNguoi(
  ten: string | null | undefined,
  id: string | null | undefined,
  nhanAi: Pick<NhanAi, 'khongRoTen' | 'khongXacDinh'>,
): { nhan: string; title?: string } {
  if (ten) return { nhan: ten, title: ten };
  return id ? { nhan: nhanAi.khongRoTen(), title: id } : { nhan: nhanAi.khongXacDinh };
}

/**
 * Thêm dòng cho giá trị đang lọc mà danh mục không có (danh mục đang tải hoặc lỗi, đổi khoảng thời gian làm người đó
 * rơi khỏi danh mục). Thiếu dòng này thì ô chọn in nguyên `value` là UUID.
 */
export function kemGiaTriDangChon(ds: readonly ChonDuoc[], value: string | undefined, nhanAi: NhanAi): ChonDuoc[] {
  if (!value || ds.some((d) => d.value === value)) return [...ds];
  return [{ value, label: nhanAi.khongRoTen(), title: value, key: value }, ...ds];
}

function ma(ds: readonly string[], nhan: (ma: string) => string, sanh: SoSanhChon): ChonDuoc[] {
  return ds.map((value) => ({ value, label: nhan(value) })).sort(sanh);
}

/**
 * Một id đổi tên ra hai dòng (tên là bản chụp) — cùng `value`, khác `key`. Hai bản ghi ra cùng một nhãn thì chỉ còn một
 * dòng: mã có trong bảng nhãn không quan tâm tới nhãn chụp.
 */
function kemNhan(ds: readonly IdKemNhan[], nhan: (d: IdKemNhan) => string, sanh: SoSanhChon): ChonDuoc[] {
  const daCo = new Set<string>();
  const ra: ChonDuoc[] = [];
  ds.forEach((d, i) => {
    const label = nhan(d);
    const khoa = `${d.id}\u0000${label}`;
    if (daCo.has(khoa)) return;
    daCo.add(khoa);
    ra.push({ value: d.id, label, key: `${d.id}#${i}` });
  });
  return ra.sort(sanh);
}

/**
 * Ô chọn người hoặc đơn vị: dòng có tên trước, dòng "Không rõ tên" xuống cuối, mã để ở `title`. Từ hai dòng không tên trở
 * lên thì kèm mã ngắn vào nhãn: nhiều dòng giống hệt nhau thì không biết chọn dòng nào.
 */
function kemTen(
  ds: readonly IdKemNhan[],
  ten: (d: IdKemNhan) => string | null | undefined,
  nhanAi: NhanAi,
  sanh: SoSanhChon,
): ChonDuoc[] {
  const coTen = kemNhan(ds.filter((d) => ten(d)), (d) => ten(d) as string, sanh);
  const idKhongTen = [...new Set(ds.filter((d) => !ten(d)).map((d) => d.id))];
  const khongTen = idKhongTen
    .map((id) => ({
      value: id,
      label: nhanAi.khongRoTen(idKhongTen.length > 1 ? id.slice(0, SO_KY_TU_MA) : undefined),
      title: id,
      key: id,
    }))
    .sort(sanh);
  return [...coTen, ...khongTen];
}

/**
 * Danh mục `/vet/bo-loc` → tuỳ chọn của các ô. Danh mục chưa về thì các ô rỗng nhưng vẫn dùng được với giá trị đang
 * đặt. Tên người và đơn vị do audit-service lấp từ danh bạ ở cả hai cổng; `danhBaDonVi` chỉ còn là nguồn phụ ở cổng tenant.
 */
export function tuyChonLocAi(
  boLoc: TuyChonLocAi | undefined,
  danhBaDonVi: ReadonlyMap<string, string>,
  nhanAi: NhanAi,
  locale = 'vi',
): TuyChonAiManHinh {
  if (!boLoc) {
    return { moHinh: [], nhaCungCap: [], service: [], tinhNang: [], nguoi: [], donVi: [], biCat: false };
  }
  const sanh = sanhTheo(locale);
  return {
    // Mã mô hình chính là tên người vận hành và khách đều đọc: giữ nguyên
    moHinh: ma(boLoc.mo_hinh, (x) => x, sanh),
    nhaCungCap: ma(boLoc.nha_cung_cap, nhanAi.nhaCungCap, sanh),
    service: ma(boLoc.service, nhanAi.service, sanh),
    tinhNang: kemNhan(
      boLoc.tinh_nang.map((t) => ({ id: t.ma, nhan: t.nhan })),
      (d) => nhanAi.tinhNang(d.id, d.nhan),
      sanh,
    ),
    nguoi: kemTen(boLoc.nguoi, (d) => d.nhan, nhanAi, sanh),
    donVi: kemTen(boLoc.don_vi, (d) => d.nhan || danhBaDonVi.get(d.id), nhanAi, sanh),
    biCat: boLoc.bi_cat === true,
  };
}

/**
 * Nhãn của một dòng bảng phân rã. `/vet/thong-ke-ai` gom theo CỘT THÔ, nên ở ba nhóm đơn vị / người dùng / tính năng
 * `khoa` là id hay mã — tra lại tên ở danh mục lọc. Không có trong danh mục: người và đơn vị là "Không rõ tên", không hiện
 * id; `(không khai)` của audit-service giữ nguyên.
 */
export function nhanKhoaPhanRa(chon: TuyChonAiManHinh, nhom: NhomPhanRa, khoa: string, nhanAi: NhanAi): string {
  if (nhom === 'theo_service') return nhanAi.service(khoa);
  if (nhom === 'theo_tinh_nang') return chon.tinhNang.find((c) => c.value === khoa)?.label ?? nhanAi.tinhNang(khoa);
  if (nhom !== 'theo_don_vi' && nhom !== 'theo_nguoi_dung') return khoa;
  const ds = nhom === 'theo_don_vi' ? chon.donVi : chon.nguoi;
  return ds.find((c) => c.value === khoa)?.label ?? (khoa.startsWith('(') ? khoa : nhanAi.khongRoTen());
}
